import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type { FrameStorageAdapter } from "./frame-storage-adapter";
import type {
	OpenFrameStore,
	OpenFrameStoreKey,
	OpenFrameStoreValue,
} from "./indexed-db-schema";

export const EPHEMERAL_STORAGE_QUERY_PARAM = "deviceStorage";
export const EPHEMERAL_STORAGE_QUERY_VALUE = "memory";

/**
 * Whether the embedding SDK requested ephemeral (in-memory) device storage
 * via the `deviceStorage=memory` query parameter on the frame URL.
 *
 * This is an explicit opt-in flag rather than user-agent detection: native
 * mobile SDKs and React Native WebViews are indistinguishable by UA, and only
 * clients that ship the flag should get the stateless behavior.
 */
export function isEphemeralDeviceStorageRequested(): boolean {
	if (typeof window === "undefined" || window.location == null) {
		return false;
	}
	const params = new URLSearchParams(window.location.search);
	return (
		params.get(EPHEMERAL_STORAGE_QUERY_PARAM) === EPHEMERAL_STORAGE_QUERY_VALUE
	);
}

/**
 * In-memory drop-in replacement for `IndexedDBAdapter`.
 *
 * Holds values (including non-extractable `CryptoKey`s, which are live
 * objects) in a `Map` for the lifetime of the page. Nothing is persisted:
 * a frame reload starts from an empty store, so the device identity is
 * regenerated and the device re-onboards (OTP) once per frame load.
 */
export class InMemoryStorageAdapter
	extends CrossmintFrameService
	implements FrameStorageAdapter
{
	name = "In-memory storage service";
	log_prefix = "[InMemoryStorage]";

	private readonly stores = new Map<OpenFrameStore, Map<string, unknown>>();

	constructor(logger: FrameLogger) {
		super(logger);
	}

	public async getItem<
		S extends OpenFrameStore,
		K extends OpenFrameStoreKey<S>,
		V extends OpenFrameStoreValue<S>,
	>(storeName: S, key: K): Promise<V | undefined> {
		this.logger.info(
			`Retrieving item from store '${storeName}' with key '${key}'`,
		);
		return this.stores.get(storeName)?.get(key) as V | undefined;
	}

	public async setItem<
		S extends OpenFrameStore,
		K extends OpenFrameStoreKey<S>,
		V extends OpenFrameStoreValue<S>,
	>(storeName: S, key: K, value: V): Promise<void> {
		this.logger.info(`Storing item in store '${storeName}' with key '${key}'`);
		let store = this.stores.get(storeName);
		if (store == null) {
			store = new Map<string, unknown>();
			this.stores.set(storeName, store);
		}
		store.set(key, value);
	}

	public async removeItem<
		S extends OpenFrameStore,
		K extends OpenFrameStoreKey<S>,
	>(storeName: S, key: K): Promise<void> {
		this.logger.info(`Removing item from '${storeName}' with key '${key}'`);
		this.stores.get(storeName)?.delete(key);
	}

	public async safeDebugState(): Promise<Record<string, unknown>> {
		return {
			mode: "in-memory",
			stores: Array.from(this.stores.entries()).map(([name, store]) => ({
				name,
				itemCount: store.size,
			})),
		};
	}
}
